export const fieldGuidance = {
  profileName: {
    title: 'Profile name',
    summary: 'A reusable label for this complete set of pricing assumptions.',
    calculate: 'Name it for the material, printer, or workflow you will recognize later.',
    benchmark: 'No standard. Favor a name that makes the profile’s purpose obvious.',
  },
  materialType: {
    title: 'Material',
    summary: 'The broad filament or resin family this profile represents.',
    calculate: 'Choose the material printed on the spool or resin bottle. Create separate profiles when materials have meaningfully different cost or failure behavior.',
    benchmark: 'No pricing standard. PLA, PETG, ABS, ASA, TPU, nylon, PC, and resin usually deserve separate profiles.',
  },
  brand: {
    title: 'Brand or product line',
    summary: 'The exact filament brand or line used by this profile.',
    calculate: 'Use the product name from the spool, invoice, or supplier listing. This makes later price updates easier.',
    benchmark: 'Optional, but useful when two products of the same material have different prices or print behavior.',
  },
  spoolPrice: {
    title: 'Spool price',
    summary: 'The amount you actually paid for the usable material on one spool.',
    calculate: 'Use the invoice price after discounts. Include shipping or tax only when you want those costs recovered through product pricing.',
    benchmark: 'There is no universal standard—use your real landed cost. For bulk purchases, divide the total order cost by the number of spools.',
  },
  spoolWeightGrams: {
    title: 'Spool weight',
    summary: 'The net weight of filament or resin purchased, excluding the empty spool or bottle.',
    calculate: 'Use the advertised net material weight and enter it in grams.',
    benchmark: 'Use the exact net weight printed on the spool, bottle, invoice, or supplier listing.',
  },
  electricityRate: {
    title: 'Electricity rate',
    summary: 'Your cost for one kilowatt-hour of electricity.',
    calculate: 'Best method: divide the electricity charges on your bill by the billed kWh. Use the all-in rate when you want delivery and energy charges recovered.',
    benchmark: 'Rates vary by utility and location. Your current bill is more accurate than a national average.',
  },
  machineRate: {
    title: 'Machine cost',
    summary: 'An hourly reserve for printer depreciation, maintenance, wear parts, and eventual replacement.',
    calculate: 'Start with: (printer and essential accessory cost − expected resale value) ÷ expected lifetime print hours, then add an hourly maintenance reserve.',
    benchmark: 'There is no universal standard. Build this rate from your own printer cost, expected service life, and maintenance history.',
  },
  laborRate: {
    title: 'Labor rate',
    summary: 'What one hour of your hands-on work must earn.',
    calculate: 'Use the hourly compensation you want for setup, slicing, support removal, cleanup, assembly, finishing, packing, and customer work. A business may also add payroll burden or overhead.',
    benchmark: 'There is no 3D-printing standard. Base the rate on what your active time must earn.',
  },
  fixedCost: {
    title: 'Fixed cost per job',
    summary: 'Costs that occur once for every job, regardless of print duration or quantity.',
    calculate: 'Add recurring per-job items such as labels, bags, tape, glue, disposable gloves, transaction minimums, or a standard setup charge.',
    benchmark: 'No standard. Leave this empty when the calculator’s job-specific “Additional batch cost” will capture everything.',
  },
  wastePercent: {
    title: 'Waste / failure allowance',
    summary: 'A reserve for failed prints, purge, test pieces, unusable parts, and other expected loss.',
    calculate: 'Best method: divide the cost of failed or wasted production by total production cost over a meaningful period, then convert to a percentage.',
    benchmark: 'Use your own production history. Stable workflows usually need less reserve than new or failure-prone work.',
  },
  targetMargin: {
    title: 'Target profit margin',
    summary: 'The percentage of the final selling price left after the production costs included in MakerMargin.',
    calculate: 'MakerMargin uses price = cost ÷ (1 − margin). Margin is the portion of the final selling price retained after the costs tracked here.',
    benchmark: 'There is no universal 3D-printing margin. Choose yours from competition, product value, fees, and business overhead.',
  },
  estimateName: {
    title: 'Estimate name',
    summary: 'The searchable name used when this calculation is saved.',
    calculate: 'Use the product name plus a useful variant, size, or customer reference.',
    benchmark: 'No standard. Specific names are easier to find and reopen later.',
  },
  quantity: {
    title: 'Quantity',
    summary: 'The number of sellable pieces produced by the entered batch.',
    calculate: 'Count only completed pieces the customer can receive. When quantity changes, MakerMargin proportionally scales the current filament and print time so material, electricity, and machine wear follow the batch. Replace those scaled values when your slicer reports more precise non-linear totals.',
    benchmark: 'Enter the number of finished, sellable pieces produced by the full batch.',
  },
  filamentGrams: {
    title: 'Filament used',
    summary: 'Total material consumed by the entire print or batch.',
    calculate: 'Use the slicer’s total grams when available. Include supports, brims, purge, and flushing when the slicer includes them.',
    benchmark: 'Slicer output is the preferred source. Actual scale measurements can improve future estimates.',
  },
  printHours: {
    title: 'Print hours',
    summary: 'Whole hours of machine time for the complete batch.',
    calculate: 'Use the slicer’s estimated time or the printer’s recorded actual duration. Put the remainder in Print minutes.',
    benchmark: 'Actual completed-job history is more reliable than a slicer estimate when the two consistently differ.',
  },
  printMinutes: {
    title: 'Print minutes',
    summary: 'The remaining minutes after the whole print hours.',
    calculate: 'Enter only the minutes remaining after the whole print hours.',
    benchmark: 'Use a value from 0 to 59.',
  },
  averagePowerWatts: {
    title: 'Average power draw',
    summary: 'Average electrical power consumed while the job is printing.',
    calculate: 'Best method: measure a representative print with a smart plug or energy meter, then divide watt-hours by print hours. Do not use the printer power supply’s maximum rating.',
    benchmark: 'Power varies heavily by printer, bed temperature, enclosure, and material. A measured value is better than a generic range.',
  },
  laborMinutes: {
    title: 'Hands-on labor',
    summary: 'Minutes when a person is actively working on this job.',
    calculate: 'Include file preparation, setup, loading material, support removal, cleanup, assembly, finishing, packing, and customer-specific administration.',
    benchmark: 'Do not count unattended print time here; that is captured by Machine cost.',
  },
  additionalCost: {
    title: 'Additional batch cost',
    summary: 'Job-specific expenses not already included in the selected cost profile.',
    calculate: 'Add special packaging, magnets, hardware, paint, inserts, commercial-license allocation, marketplace fees, or outsourced work for this batch.',
    benchmark: 'No standard. Avoid entering a cost here if it is already included in Fixed cost per job.',
  },
};
